import React, { useEffect, useState } from 'react';

const listeners = new Set<() => void>();

export function navigate(to: string, { replace = false } = {}) {
  if (replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  listeners.forEach(fn => fn());
  if (!to.includes('#')) window.scrollTo(0, 0);
}

export function useLocation() {
  const [, force] = useState(0);
  useEffect(() => {
    const update = () => force(n => n + 1);
    listeners.add(update);
    window.addEventListener('popstate', update);
    return () => {
      listeners.delete(update);
      window.removeEventListener('popstate', update);
    };
  }, []);
  return { path: window.location.pathname, search: new URLSearchParams(window.location.search) };
}

type LinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & { to: string };

// Client-side link: plain clicks navigate without a reload; modified clicks behave normally.
export function Link({ to, onClick, children, ...rest }: LinkProps) {
  return (
    <a
      href={to}
      onClick={e => {
        onClick?.(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (to.startsWith('/#')) {
          if (window.location.pathname === '/') return;
        }
        e.preventDefault();
        navigate(to);
        const hash = to.split('#')[1];
        if (hash) requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }));
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
